<template>
  <el-dialog
    :title="`${action === 'add' ? 'Create' : 'Edit'} API Key`"
    :visible.sync="setShow"
    :close-on-click-modal="false"
    :show-close="false"
    width="40%"
  >
    <template v-if="showApiToken">
      <div class="ra-api">
        <el-alert title="Your API Key" type="success" :closable="false">
          <div class="ra-api--key">
            <h6>{{ apiToken }}</h6>
            <span
              class="copy"
              v-clipboard:copy="apiToken"
              v-clipboard:success="onCopy"
            >
              <i class="ra-icon--copy"></i>
            </span>
          </div>
        </el-alert>
        <p>
          <strong>Note - </strong>API Key should be copied and saved as you
          won’t be after to view after you leave this page.
        </p>
      </div>
    </template>
    <template v-else>
      <el-row type="flex" v-loading="loading">
        <el-col :span="24">
          <el-form :model="form" label-position="top" ref="form">
            <el-row type="flex">
              <el-col :span="24">
                <el-form-item
                  v-custom-input="form.name"
                  label="API Key Name"
                  prop="name"
                  :rules="validateField()"
                >
                  <el-input v-model="form.name" />
                </el-form-item>
              </el-col>
            </el-row>
            <el-row type="flex">
              <el-col :span="24">
                <el-form-item
                  label="Set Permissions"
                  prop="role_ids"
                  :rules="validateField('At least one role ')"
                >
                  <el-checkbox-group v-model="form.role_ids">
                    <el-checkbox
                      v-for="role in permissions"
                      :key="role.id"
                      :label="role.id"
                      >{{ role.name }}</el-checkbox
                    >
                  </el-checkbox-group>
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>
        </el-col>
      </el-row>
    </template>
    <span slot="footer">
      <template v-if="showApiToken">
        <el-button type="primary" size="medium" @click="close"
          >Complete</el-button
        >
      </template>
      <template v-else>
        <el-button type="primary" plain size="medium" @click="close"
          >Cancel</el-button
        >
        <el-button
          type="primary"
          size="medium"
          :loading="saving"
          @click="save"
          >{{ action === "edit" ? "Update" : "Create" }}</el-button
        >
      </template>
    </span>
  </el-dialog>
</template>

<script>
import apiConstants from "@/apiConstants.js";

export default {
  name: "ApiKeyForm",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    action: {
      type: String,
      default: "add"
    },
    data: {
      type: Object,
      default() {
        return {};
      }
    }
  },

  data() {
    return {
      loading: false,
      form: {
        name: "",
        role_ids: []
      },
      saving: false,
      apiToken: "",
      showApiToken: false,
      permissions: []
    };
  },

  computed: {
    setShow: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      }
    }
  },

  watch: {
    show() {
      if (this.show) {
        if (!this.permissions.length) {
          this.getRoleGroups();
        }

        if (this.$refs.form) {
          this.$refs.form.resetFields();
        }
      }

      if (this.action === "edit") {
        this.form.name = this.data.name;
        this.getApiKeyRoles();
      }
    }
  },

  methods: {
    getRoleGroups() {
      this.loading = true;
      const apiURL = "roles/groups";
      const filters = [];
      filters.push({
        field: "user_type_id",
        value: apiConstants.USER_TYPES.NORMAL
      });
      const params = `?no_pages=1&filters=${JSON.stringify(filters)}`;

      this.$http
        .post(apiURL, {
          method: "GET",
          params: params
        })
        .then(response => {
          const resData = response.data;
          if (resData.status === "error") {
            this.handleErrors(resData);
          } else {
            this.permissions = resData.list;
          }
          this.loading = false;
        })
        .catch(() => {
          this.notification("An unknown error occurred", "danger");
          this.loading = false;
        });
    },
    reset() {
      if (this.$refs.form) {
        this.$refs.form.resetFields();
      }
      this.apiToken = "";
      this.showApiToken = false;
    },
    close() {
      this.setShow = false;
      this.reset();
    },
    getApiKeyRoles() {
      this.loading = true;
      const keyId = this.data.id;
      const apiUrl = `api/clients/${keyId}/roles`;
      this.$http
        .post(apiUrl, {
          method: "GET"
        })
        .then(response => {
          const resData = response.data;
          if (resData.status === "error") {
            this.handleErrors(resData);
            this.loading = true;
          } else {
            this.form.role_ids = resData.map(el => el.role.group_id);
            this.loading = false;
          }
        })
        .catch(() => {
          this.loading = false;
          this.notification("An unknown error occurred", "danger");
        });
    },

    save() {
      this.saving = true;
      this.$refs.form.validate(async valid => {
        if (!valid) {
          this.saving = false;
          return false;
        }

        const httpVerb = this.action === "edit" ? "PUT" : "POST";
        let apiUrl = "api/clients";
        let keyId = null;
        if (httpVerb === "PUT") {
          keyId = this.data.id;
          apiUrl += `/${this.data.id}`;
        }

        this.$http
          .post(apiUrl, {
            body: this.form,
            method: httpVerb
          })
          .then(response => {
            this.saving = false;
            const resData = response.data;
            if (resData.status === "error") {
              this.handleErrors(resData);
            } else {
              this.notification(
                "Completed",
                `API Key ${keyId ? "updated" : "created"} successfully`,
                "success"
              );
              this.$emit("success");

              if (this.action === "add") {
                this.apiToken = resData.api_token;
                this.showApiToken = true;
                this.$refs.form.resetFields();
              } else {
                this.close();
              }
            }
          })
          .catch(() => {
            this.notification("Error", "An unknown error occurred", "danger");
            this.saving = false;
          });
      });
    },

    onCopy() {
      this.$message({
        message: `Copied`,
        type: "success"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.ra-api {
  &--key {
    display: flex;
    align-items: center;

    span {
      margin-left: 10px;
      padding: 10px;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      background: #14050810;
      border-radius: 100px;

      i {
        font-weight: 600;
        color: #140508;
        font-size: 1.1rem;
      }
    }
  }

  p {
    font-size: 0.875rem;
    color: var(--ra-grey-tertiary);
    margin-top: 10px;
  }
}
</style>
